import {
  Children,
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export interface TooltipProps {
  /** One short, localized action label (five words maximum). */
  readonly label: string;
  /** The single interactive element that owns the tooltip. */
  readonly children: ReactElement;
  readonly placement?: "bottom" | "top" | undefined;
}

interface TooltipTriggerProps {
  readonly "aria-describedby"?: string | undefined;
  readonly onBlur?: ((event: FocusEvent<HTMLElement>) => void) | undefined;
  readonly onClick?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  readonly onFocus?: ((event: FocusEvent<HTMLElement>) => void) | undefined;
  readonly onKeyDown?: ((event: KeyboardEvent<HTMLElement>) => void) | undefined;
  readonly onMouseEnter?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  readonly onMouseLeave?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  readonly onPointerDown?: ((event: PointerEvent<HTMLElement>) => void) | undefined;
}

interface TooltipPosition extends CSSProperties {
  readonly visibility: "hidden" | "visible";
}

const HIDDEN_POSITION: TooltipPosition = {
  left: 0,
  top: 0,
  visibility: "hidden",
};

function tokenPixels(name: string): number {
  if (typeof document === "undefined") return 0;
  const rootStyle = getComputedStyle(document.documentElement);
  const rawValue = rootStyle.getPropertyValue(name).trim();
  const value = Number.parseFloat(rawValue);
  if (!Number.isFinite(value)) return 0;
  if (!rawValue.endsWith("rem")) return value;
  const rootFontSize = Number.parseFloat(rootStyle.fontSize);
  return Number.isFinite(rootFontSize) ? value * rootFontSize : value;
}

/**
 * Accessible, non-interactive action hint. It opens on hover, keyboard focus,
 * and touch press, and is portalled so panel overflow cannot clip it.
 */
export function Tooltip({ children, label, placement = "top" }: TooltipProps): ReactNode {
  const id = `${useId()}-tooltip`;
  const anchorRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [touchOpen, setTouchOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [effectivePlacement, setEffectivePlacement] = useState<"bottom" | "top">(placement);
  const [position, setPosition] = useState<TooltipPosition>(HIDDEN_POSITION);
  const visible = !dismissed && (focused || hovered || touchOpen);

  useLayoutEffect(() => {
    if (!visible || anchorRef.current === null || tooltipRef.current === null) return;

    const updatePosition = (): void => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      const tooltip = tooltipRef.current?.getBoundingClientRect();
      if (anchor === undefined || tooltip === undefined) return;

      const viewportPadding = tokenPixels("--thw-space-sm");
      const requestedTop = placement === "top";
      const roomAbove = anchor.top;
      const roomBelow = window.innerHeight - anchor.bottom;
      const useTop = requestedTop
        ? roomAbove >= tooltip.height || roomAbove >= roomBelow
        : !(roomBelow >= tooltip.height || roomBelow >= roomAbove);
      const centre = anchor.left + anchor.width / 2;
      const halfWidth = tooltip.width / 2;
      const minimumLeft = viewportPadding + halfWidth;
      const maximumLeft = window.innerWidth - viewportPadding - halfWidth;

      setEffectivePlacement(useTop ? "top" : "bottom");
      setPosition({
        left: Math.max(minimumLeft, Math.min(centre, maximumLeft)),
        top: useTop ? anchor.top : anchor.bottom,
        visibility: "visible",
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePosition);
    if (observer !== null) {
      observer.observe(anchorRef.current);
      observer.observe(tooltipRef.current);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      observer?.disconnect();
    };
  }, [label, placement, visible]);

  useEffect(() => {
    if (!touchOpen) return;
    const closeOutside = (event: globalThis.PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && anchorRef.current?.contains(target)) return;
      setTouchOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () => { document.removeEventListener("pointerdown", closeOutside, true); };
  }, [touchOpen]);

  useEffect(() => {
    if (!visible) setPosition(HIDDEN_POSITION);
  }, [visible]);

  const trigger = Children.only(children) as ReactElement<TooltipTriggerProps>;
  const describedBy = [trigger.props["aria-describedby"], id].filter(Boolean).join(" ");
  const enhancedTrigger = cloneElement(trigger, {
    "aria-describedby": describedBy,
    onBlur: (event: FocusEvent<HTMLElement>) => {
      trigger.props.onBlur?.(event);
      setFocused(false);
    },
    onClick: (event: MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event);
      // Activation means the hint has served its purpose. This is especially
      // important on touch, where the browser leaves the button focused after
      // the tap and would otherwise pin a portalled tooltip over the form.
      setDismissed(true);
      setTouchOpen(false);
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      trigger.props.onFocus?.(event);
      anchorRef.current = event.currentTarget;
      setDismissed(false);
      setFocused(true);
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      trigger.props.onKeyDown?.(event);
      if (event.key !== "Escape") return;
      setDismissed(true);
      setFocused(false);
      setHovered(false);
      setTouchOpen(false);
    },
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      trigger.props.onMouseEnter?.(event);
      anchorRef.current = event.currentTarget;
      setDismissed(false);
      setHovered(true);
    },
    onMouseLeave: (event: MouseEvent<HTMLElement>) => {
      trigger.props.onMouseLeave?.(event);
      setHovered(false);
    },
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      trigger.props.onPointerDown?.(event);
      anchorRef.current = event.currentTarget;
      if (event.pointerType === "touch") {
        setDismissed(false);
        setTouchOpen(true);
      }
    },
  });

  const tooltip = !visible ? null : (
    <span
      className="thw-tooltip"
      data-placement={effectivePlacement}
      id={id}
      ref={tooltipRef}
      role="tooltip"
      style={position}
    >
      {label}
    </span>
  );

  return (
    <>
      {enhancedTrigger}
      {typeof document === "undefined" || tooltip === null ? tooltip : createPortal(tooltip, document.body)}
    </>
  );
}
